/**
 * useFacets Hook
 * 
 * Fetches and caches dynamic facets from the backend for filter UI.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../auth';
import type { FacetsResponse, FacetField, FacetValue } from '../types/search';
import {
  buildCoOccurrenceIndex,
  type CoOccurrenceIndex,
} from '../utils/cooccurrenceIndex';

interface UseFacetsResult {
  facets: FacetField[];
  loading: boolean;
  error: string | null;
  /**
   * O(1) lookup index over the static facet co-occurrence map. Used by
   * filter UIs to grey out values whose selection would yield zero
   * results given the currently active filters.
   */
  cooccurrenceIndex: CoOccurrenceIndex;
  /** Get a specific facet field by name */
  getFacetField: (fieldName: string) => FacetField | undefined;
  /** Get display name for a value within a facet field */
  getDisplayName: (fieldName: string, value: string) => string;
  /** Refresh facets from backend */
  refresh: () => Promise<void>;
}

// Audience scope for facets requested by the search frontend. Matches the
// value used elsewhere (e.g. suggestions) and gates field-level visibility
// on the backend: fields whose `visible_for_audiences` excludes
// `search_frontend` (currently contributor type/role) are omitted.
const FRONTEND_AUDIENCE = 'search_frontend';

// Module-level cache to avoid refetching on every component mount
let cachedFacets: FacetsResponse | null = null;
let cacheExpiry: Date | null = null;

export function useFacets(): UseFacetsResult {
  const { isAuthenticated, accessToken } = useAuth();
  const [facets, setFacets] = useState<FacetField[]>(cachedFacets?.facets ?? []);
  const [cooccurrence, setCooccurrence] = useState(
    cachedFacets?.co_occurrence ?? null,
  );
  const [loading, setLoading] = useState(!cachedFacets);
  const [error, setError] = useState<string | null>(null);

  const fetchFacets = useCallback(async () => {
    // Check if cache is still valid
    if (cachedFacets && cacheExpiry && new Date() < cacheExpiry) {
      setFacets(cachedFacets.facets);
      setCooccurrence(cachedFacets.co_occurrence ?? null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getFacets(FRONTEND_AUDIENCE);
      cachedFacets = response;
      
      // Set cache expiry (use backend's expiry or default to 5 minutes)
      if (response.expires_at) {
        cacheExpiry = new Date(response.expires_at);
      } else {
        cacheExpiry = new Date(Date.now() + 5 * 60 * 1000);
      }
      
      setFacets(response.facets);
      setCooccurrence(response.co_occurrence ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load facets';
      setError(message);
      console.error('Failed to fetch facets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFacets();
  }, [fetchFacets]);

  // Refetch facets whenever the auth identity changes (login or logout).
  // The backend returns a different facet value set per identity (e.g. the
  // employee-only `source=sunrise` value is hidden from anonymous users), so
  // the module-level cache must be invalidated and refetched. Because the
  // cache is shared by all consumers, clearing it once refreshes everywhere.
  const isFirstAuthRun = useRef(true);
  useEffect(() => {
    // Ensure the API client carries the current token before refetching.
    // setAccessToken is sync, so doing it here avoids any race with the
    // separate effect in HomePage (calling it twice is idempotent).
    apiService.setAccessToken(accessToken);
    if (isFirstAuthRun.current) {
      isFirstAuthRun.current = false;
      return; // initial mount already fetched via the existing effect
    }
    cachedFacets = null;
    cacheExpiry = null;
    fetchFacets();
  }, [isAuthenticated, accessToken, fetchFacets]);

  const getFacetField = useCallback((fieldName: string): FacetField | undefined => {
    return facets.find(f => f.field_name === fieldName);
  }, [facets]);

  const getDisplayName = useCallback((fieldName: string, value: string): string => {
    const field = facets.find(f => f.field_name === fieldName);
    if (!field) return value;
    
    const facetValue = field.values.find(v => v.value === value);
    return facetValue?.display_name ?? value;
  }, [facets]);

  const refresh = useCallback(async () => {
    // Clear cache to force refresh
    cachedFacets = null;
    cacheExpiry = null;
    await fetchFacets();
  }, [fetchFacets]);

  const cooccurrenceIndex = useMemo(
    () => buildCoOccurrenceIndex(cooccurrence, facets),
    [cooccurrence, facets],
  );

  return {
    facets,
    loading,
    error,
    cooccurrenceIndex,
    getFacetField,
    getDisplayName,
    refresh,
  };
}

/**
 * Get values for a specific facet field, sorted by priority
 */
export function getFacetValues(field: FacetField | undefined): FacetValue[] {
  if (!field) return [];
  return [...field.values].sort((a, b) => a.priority - b.priority);
}
